import React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { folderService, type FolderTreeNode } from "@/services/folderService";
import { aiAssistantService } from "@/services/aiAssistantService";
import { useToast } from "@/hooks/use-toast";
import { Folder as FolderIcon } from "lucide-react";

interface FolderAssignDropdownProps {
  outputId: string;
  currentFolderId?: string | null;
  className?: string;
  onChange?: (folderId: string | null) => void;
}

export function FolderAssignDropdown({
  outputId,
  currentFolderId,
  className = "",
  onChange,
}: FolderAssignDropdownProps) {
  const { organization } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const orgId = organization?.id || "";

  const { data: folderTree } = useQuery({
    queryKey: ["folderTree", orgId, false],
    queryFn: async () => {
      if (!orgId) return [] as FolderTreeNode[];
      const { data } = await folderService.getFolderTree(orgId, false);
      return data || [];
    },
    enabled: !!orgId,
  });

  const moveMutation = useMutation({
    mutationFn: async (folderId: string | null) => {
      const { error } = await aiAssistantService.moveOutputToFolder(
        outputId,
        folderId,
      );
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          (q.queryKey as any[])[0] === "folderOutputs",
      });
      await queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          (q.queryKey as any[])[0] === "organizationOutputs",
      });
      await queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          (q.queryKey as any[])[0] === "folderTree",
      });
      toast({ title: "Saved", description: "Output location updated." });
    },
  });

  const label = React.useMemo(() => {
    if (!currentFolderId) return "Add to folder";
    const node = (folderTree || []).find((n) => n.id === currentFolderId);
    if (!node) return "Add to folder";
    return node.path.join(" / ");
  }, [currentFolderId, folderTree]);

  const renderMenuItems = (
    nodes: FolderTreeNode[],
    parentId?: string,
    depth: number = 0,
  ) => {
    const levelNodes = nodes.filter((n) =>
      parentId ? n.parent_folder_id === parentId : n.parent_folder_id === null,
    );
    return levelNodes.map((n) => {
      const hasChildren = nodes.some((c) => c.parent_folder_id === n.id);
      if (hasChildren) {
        return (
          <DropdownMenuSub key={n.id}>
            <DropdownMenuSubTrigger>
              <span style={{ paddingLeft: depth * 12 }}>{n.name}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent
              sideOffset={4}
              alignOffset={-4}
              className="min-w-[220px]"
            >
              <DropdownMenuItem
                onSelect={() => {
                  onChange?.(n.id);
                  moveMutation.mutate(n.id);
                }}
              >
                Select “{n.name}”
              </DropdownMenuItem>
              {renderMenuItems(nodes, n.id, depth + 1)}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        );
      }
      return (
        <DropdownMenuItem
          key={n.id}
          onSelect={() => {
            onChange?.(n.id);
            moveMutation.mutate(n.id);
          }}
        >
          <span style={{ paddingLeft: depth * 12 }}>{n.name}</span>
        </DropdownMenuItem>
      );
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`flex items-center gap-2 ${className}`}
        >
          <FolderIcon className="h-4 w-4" />
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={4} className="min-w-[220px]">
        {currentFolderId && (
          <DropdownMenuItem
            onClick={() => {
              onChange?.(null);
              moveMutation.mutate(null);
            }}
          >
            Remove from folder
          </DropdownMenuItem>
        )}
        <div className="h-px my-1 bg-gray-200" />
        {renderMenuItems(folderTree || [])}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
